import React, { useEffect, useState } from "react";
import {
  CalendarClock,
  Globe,
  Mail,
  MessageSquare,
  Save,
  Tag,
  User,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../Modal/Modal";
import Input from "../Input/Input";

/* ===================== SERVICES ===================== */
import { apiPatch } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";
import { can } from "../../utils/permissions";

/* ===================== STYLES (shared with Help Desk) ===================== */
import styles from "../HelpDesk/HelpDeskShared.module.scss";

/* Statuses an admin is allowed to set. "New" is the initial server state. */
const STATUS_OPTIONS = [
  { label: "Read", value: "Read" },
  { label: "Responded", value: "Responded" },
  { label: "Archived", value: "Archived" },
];

const statusTone = (status) => {
  switch (status) {
    case "New":
      return "open";
    case "Read":
      return "progress";
    case "Responded":
      return "resolved";
    case "Archived":
      return "closed";
    default:
      return "closed";
  }
};

/* createdAt arrives pre-formatted ("2026-06-15 04:50 PM"); updatedAt is ISO. */
const formatMaybeIso = (value) => {
  if (!value) return "--";
  const str = String(value);
  const isIso = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) || str.endsWith("Z");
  if (!isIso) return str;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ViewContactRequestModal = ({
  open,
  request,
  onClose,
  onError,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (open) {
      setStatus(request?.status || "");
    }
  }, [open, request]);

  if (!open || !request) return null;

  /* Ensure the select can always represent the current status, even if it's
     "New" (which is not an admin-settable option). */
  const options = STATUS_OPTIONS.some((o) => o.value === request.status)
    ? STATUS_OPTIONS
    : [{ label: request.status, value: request.status }, ...STATUS_OPTIONS];

  const handleStatusSave = async () => {
    if (!request.id || !status || status === request.status) return;
    setSubmitting(true);
    try {
      const res = await apiPatch(
        "catalog",
        `/contact-requests/${request.id}/status`,
        { status },
      );
      if (res?.status === "success") {
        onSuccess?.(res?.message || "Status updated");
        onClose?.();
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update status",
          }),
        );
      }
    } catch (err) {
      onError?.(getErrorMessage(err, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  const tone = statusTone(request.status);

  return (
    <Modal title="Contact Request Details" onClose={onClose}>
      <div className={styles["helpdesk-modal__container"]}>
        {/* SUMMARY */}
        <div className={styles["helpdesk-modal__section"]}>
          <div className={styles["helpdesk-modal__section-header"]}>
            <div className={styles["helpdesk-modal__icon-wrapper"]}>
              <Tag size={18} strokeWidth={2.5} />
            </div>
            <h3 className={styles["helpdesk-modal__section-title"]}>
              Request #{request.id}
            </h3>
            <span
              className={`${styles["helpdesk-modal__status-badge"]} ${
                styles[`helpdesk-modal__status-badge--${tone}`]
              }`}
            >
              {request.status || "--"}
            </span>
          </div>

          <h4 className={styles["helpdesk-modal__subject"]}>
            {request.fullName || "--"}
          </h4>
          <p className={styles["helpdesk-modal__description"]}>
            {request.message || "No message provided."}
          </p>
        </div>

        {/* CONTACT */}
        <div className={styles["helpdesk-modal__section"]}>
          <div className={styles["helpdesk-modal__section-header"]}>
            <div className={styles["helpdesk-modal__icon-wrapper"]}>
              <User size={18} strokeWidth={2.5} />
            </div>
            <h3 className={styles["helpdesk-modal__section-title"]}>Contact</h3>
          </div>
          <div className={styles["helpdesk-modal__grid"]}>
            <div className={styles["helpdesk-modal__field"]}>
              <span className={styles["helpdesk-modal__field-label"]}>
                Email
              </span>
              <a
                href={`mailto:${request.email}`}
                className={styles["helpdesk-modal__link"]}
              >
                <Mail size={14} strokeWidth={2.5} />
                {request.email || "--"}
              </a>
            </div>
            <Field label="Reason" value={request.reason} />
            <div className={styles["helpdesk-modal__field"]}>
              <span className={styles["helpdesk-modal__field-label"]}>
                IP Address
              </span>
              <span className={styles["helpdesk-modal__field-value"]}>
                <Globe
                  size={13}
                  strokeWidth={2.5}
                  style={{ marginRight: 6, verticalAlign: "-2px" }}
                />
                {request.ipAddress || "--"}
              </span>
            </div>
          </div>
        </div>

        {/* TIMELINE */}
        <div className={styles["helpdesk-modal__section"]}>
          <div className={styles["helpdesk-modal__section-header"]}>
            <div className={styles["helpdesk-modal__icon-wrapper"]}>
              <CalendarClock size={18} strokeWidth={2.5} />
            </div>
            <h3 className={styles["helpdesk-modal__section-title"]}>Timeline</h3>
          </div>
          <div className={styles["helpdesk-modal__grid"]}>
            <Field label="Created" value={formatMaybeIso(request.createdAt)} />
            <Field
              label="Last Updated"
              value={formatMaybeIso(request.updatedAt)}
            />
          </div>
        </div>

        {/* STATUS UPDATE */}
        {can("contact-requests", "update") && (
          <div className={styles["helpdesk-modal__section"]}>
            <div className={styles["helpdesk-modal__section-header"]}>
              <div className={styles["helpdesk-modal__icon-wrapper"]}>
                <MessageSquare size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-modal__section-title"]}>
                Update Status
              </h3>
            </div>

            <div className={styles["helpdesk-modal__status-row"]}>
              <Input
                type="select"
                label="Status"
                name="status"
                value={status}
                options={options}
                onChange={(e) => setStatus(e.target.value)}
              />
              <button
                type="button"
                className={`${styles["helpdesk-modal__btn"]} ${styles["helpdesk-modal__btn--primary"]}`}
                onClick={handleStatusSave}
                disabled={submitting || !status || status === request.status}
              >
                <Save size={16} strokeWidth={2.5} />
                {submitting ? "Updating…" : "Save Status"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const Field = ({ label, value }) => (
  <div className={styles["helpdesk-modal__field"]}>
    <span className={styles["helpdesk-modal__field-label"]}>{label}</span>
    <span className={styles["helpdesk-modal__field-value"]}>
      {value || value === 0 ? String(value) : "--"}
    </span>
  </div>
);

export default ViewContactRequestModal;
